import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { Overlap } from '../types';
import {
  getSeenOverlaps,
  markOverlapsSeen,
  computeOverlapFingerprint,
} from '../services/notificationStore';

export interface ToastData {
  id: string;
  message: string;
  subtext?: string;
}

export function useOverlapNotifications(
  overlaps: Overlap[],
  currentUserId: string,
  mutedFriendIds: Set<string>,
  rosterActive: boolean,
  baseAirport: string
) {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const processedRef = useRef<Set<string>>(new Set());
  const shownThisSession = useRef(false);

  // Filter overlaps: exclude muted friends AND home-base locals
  const relevantOverlaps = useMemo(
    () =>
      overlaps.filter((o) => {
        if (o.friends.every((f) => mutedFriendIds.has(f.user.id))) return false;
        if (o.city === baseAirport && o.friends.every((f) => f.reason === 'free_at_base')) return false;
        return true;
      }),
    [overlaps, mutedFriendIds, baseAirport]
  );

  // Compute new (unseen) overlaps using fingerprints
  const newOverlapFingerprints = useMemo(() => {
    const seen = getSeenOverlaps(currentUserId);
    return relevantOverlaps
      .filter((o) => !seen.has(computeOverlapFingerprint(o)))
      .map((o) => computeOverlapFingerprint(o));
  }, [relevantOverlaps, currentUserId]);

  const unseenCount = newOverlapFingerprints.length;

  // Debounced single toast — waits 2s for overlaps to stabilize,
  // then shows ONE consolidated notification per session
  useEffect(() => {
    if (!rosterActive) return;

    const timer = setTimeout(() => {
      // Only show one toast per app session to avoid spamming
      if (shownThisSession.current) return;

      const seen = getSeenOverlaps(currentUserId);
      const brandNew = relevantOverlaps.filter((o) => {
        const fp = computeOverlapFingerprint(o);
        return !seen.has(fp) && !processedRef.current.has(fp);
      });

      if (brandNew.length === 0) return;

      const cities = [...new Set(brandNew.map((o) => o.city))];
      const friendNames = [
        ...new Set(
          brandNew.flatMap((o) =>
            o.friends
              .filter((f) => !mutedFriendIds.has(f.user.id))
              .filter((f) => !(o.city === baseAirport && f.reason === 'free_at_base'))
              .map((f) => f.user.name.split(' ')[0])
          )
        ),
      ];

      let message: string;
      if (friendNames.length === 1) {
        message = `${brandNew.length} overlap${brandNew.length > 1 ? 's' : ''} with ${friendNames[0]}`;
      } else if (friendNames.length <= 3) {
        message = `${brandNew.length} overlaps with ${friendNames.join(', ')}`;
      } else {
        message = `${brandNew.length} overlaps with ${friendNames.length} friends`;
      }

      const subtext = cities.length <= 3
        ? cities.join(', ')
        : `${cities.slice(0, 3).join(', ')} +${cities.length - 3} more`;

      // Stable ID so React doesn't unmount mid-swipe
      const consolidatedToast: ToastData = {
        id: 'overlap-toast',
        message,
        subtext,
      };

      const fps: string[] = [];
      for (const o of brandNew) {
        const fp = computeOverlapFingerprint(o);
        processedRef.current.add(fp);
        fps.push(fp);
      }

      shownThisSession.current = true;
      setToasts([consolidatedToast]);

      // Mark as seen after toast auto-dismisses
      const seenTimer = setTimeout(() => {
        markOverlapsSeen(currentUserId, fps);
      }, 5000);

      return () => clearTimeout(seenTimer);
    }, 2000); // 2s debounce — let data settle

    return () => clearTimeout(timer);
  }, [relevantOverlaps, currentUserId, mutedFriendIds, rosterActive, baseAirport]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const markAllSeen = useCallback(() => {
    const fps = relevantOverlaps.map((o) => computeOverlapFingerprint(o));
    markOverlapsSeen(currentUserId, fps);
    for (const fp of fps) {
      processedRef.current.add(fp);
    }
    setToasts([]);
  }, [relevantOverlaps, currentUserId]);

  return {
    newOverlaps: relevantOverlaps.filter((o) =>
      newOverlapFingerprints.includes(computeOverlapFingerprint(o))
    ),
    toasts,
    dismissToast,
    markAllSeen,
    unseenCount,
  };
}
